import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';

const useDashboardMain = () => {
  const { user } = useAuth();
  const displayName = user?.fullName || user?.name || 'USER';
  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  
  const handleFilterChange = (e) => setSelectedProject(e.target.value);
  const isMounted = useRef(true);
  const fetchedForUserId = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    
    const fetchDashboardData = async () => {
      if (!user) return;
      
      // Only fetch once per unique user id - prevents double fetch from background validation update
      const userId = user.id || user.userId;
      if (fetchedForUserId.current === userId) {
        return;
      }
      
      fetchedForUserId.current = userId;
      const abortController = new AbortController();
      
      try {
        setLoading(true);
        setError(null);
        
        let response;
        
        if (user && (user.role === 'site_engineer' || user.role === 'site_manager' || user.role === 'project_manager')) {
          response = await api.get('/users/me/projects', { signal: abortController.signal });
          const projectsData = response.data || [];
          
          const nonArchivedProjects = projectsData.filter(p => !p.isArchived);
          const active = nonArchivedProjects.filter(p => 
            p.status === 'in_progress' || p.status === 'planning' || p.status === 'on_hold'
          );
          const completed = nonArchivedProjects.filter(p => 
            p.status === 'completed' || p.status === 'closed'
          );
          
          if (isMounted.current) {
            setProjects(projectsData);
            setActiveProjects(active);
            setCompletedProjects(completed);
          }
        } else {
          response = await api.get('/projects', { signal: abortController.signal });
          const projectsData = response.data.projects || [];
          
          let userProjects = projectsData;
          
          if (user && user.role) {
            if (user.role === 'customer') {
              userProjects = projectsData.filter(project => 
                project.clientId === user.id || 
                project.projectClientId === user.id ||
                (project.members && project.members.some(member => 
                  member.userId === user.id && member.role === 'customer'
                ))
              );
            }
          }
          
          const nonArchivedProjects = userProjects.filter(p => !p.isArchived);
          const active = nonArchivedProjects.filter(p => 
            p.status === 'in_progress' || p.status === 'planning' || p.status === 'on_hold'
          );
          const completed = nonArchivedProjects.filter(p => 
            p.status === 'completed' || p.status === 'closed'
          );
          
          if (isMounted.current) {
            setProjects(userProjects);
            setActiveProjects(active);
            setCompletedProjects(completed);
          }
        }
        
        if (isMounted.current) {
          setTodayTasks([]);
          setUpcomingDeadlines([]);
        }
        
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        console.error('Error fetching dashboard data:', err);
        if (isMounted.current) {
          setProjects([]);
          setActiveProjects([]);
          setCompletedProjects([]);
          setTodayTasks([]);
          setUpcomingDeadlines([]);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };
    
    if (user) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [user]);

  return {
    displayName,
    loading,
    projects,
    activeProjects,
    completedProjects,
    todayTasks,
    upcomingDeadlines,
    error,
    selectedProject,
    handleFilterChange
  };
};

export default useDashboardMain;
